import { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, DollarSign, MapPin, User, type LucideIcon } from "lucide-react";
import { type JobPosting } from "@/lib/services";
import { JobVisual } from "@/components/jobs/JobVisual";

export interface JobDetailAction {
  label: string;
  onClick: () => void;
  icon: LucideIcon;
  variant?: "primary" | "secondary";
}

interface JobDetailPresentationProps {
  job: JobPosting;
  primaryAction: JobDetailAction;
  secondaryAction: JobDetailAction;
  extraActions?: JobDetailAction[];
  splitDetails?: boolean;
  creatorLabel?: string;
  sidebarExtra?: ReactNode;
  footer?: ReactNode;
}

const formatJobType = (jobType: string) => {
  const typeMap: { [key: string]: string } = {
    instruction: "Dance Instructor",
    substitute: "Substitute Instructor",
    "master-class": "Master Class",
    "guest-choreographer": "Guest Choreographer",
    "judge-adjudicator": "Judge/Adjudicator",
    other: "Other",
  };
  return typeMap[jobType] || jobType || "Job Posting";
};

export const formatCreatorName = (fullName: string) => {
  if (!fullName) return "Dance Company";
  const nameParts = fullName.trim().split(" ");
  if (nameParts.length === 1) return fullName;
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];
  const middleNames = nameParts.slice(1, -1);
  let formattedName = firstName;
  if (middleNames.length > 0) {
    formattedName += ` ${middleNames.map((name) => `${name.charAt(0)}.`).join(" ")}`;
  }
  formattedName += ` ${lastName.charAt(0)}.`;
  return formattedName;
};

export const formatCompensation = (compensation: any) => {
  if (!compensation) return "Not specified";
  if (compensation.type === "negotiable") return "Negotiable";

  const { minRate, maxRate, type } = compensation;
  if (!minRate || minRate === 0) return "Not specified";

  let rateDisplay = `$${minRate}`;
  if (maxRate && maxRate !== minRate) {
    rateDisplay += ` - $${maxRate}`;
  }

  switch (type) {
    case "hourly":
      return `${rateDisplay} per hour`;
    case "per-session":
      return `${rateDisplay} per session`;
    case "flat-rate":
      return `${rateDisplay} flat rate`;
    default:
      return rateDisplay;
  }
};

const formatPostedDate = (createdAt: any) => {
  try {
    if (!createdAt) return "Recently";
    if (typeof createdAt === "object" && "seconds" in createdAt) {
      return new Date(createdAt.seconds * 1000);
    }
    const parsed = new Date(createdAt);
    return Number.isNaN(parsed.getTime()) ? "Recently" : parsed;
  } catch {
    return "Recently";
  }
};

const formatReadableDate = (createdAt: any) => {
  const postedDate = formatPostedDate(createdAt);
  if (postedDate === "Recently") return "Recently";
  return (postedDate as Date).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatPostedAgo = (createdAt: any) => {
  const postedDate = formatPostedDate(createdAt);
  if (postedDate === "Recently") return "Posted recently";
  const date = postedDate as Date;
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  if (diffDays === 0) return "Posted today";
  if (diffDays === 1) return "Posted 1 day ago";
  if (diffDays < 30) return `Posted ${diffDays} days ago`;
  return `Posted ${formatReadableDate(createdAt)}`;
};

const getLocationDisplay = (job: JobPosting) => {
  if (job.locationType === "virtual") return "Remote";
  if (job.location?.city && job.location?.state) {
    return `${job.location.city}, ${job.location.state}`;
  }
  return "Location TBD";
};

const getLocationQuery = (job: JobPosting) => {
  const parts = [
    job.location?.venue,
    job.location?.address,
    job.location?.city,
    job.location?.state,
    job.location?.zipCode,
  ].filter(Boolean);

  if (parts.length === 0) {
    return "";
  }

  return parts.join(", ");
};

const getScheduleDisplay = (job: JobPosting) => {
  if (job.scheduleType === "recurring") return "Recurring";
  if (job.scheduleType) return job.scheduleType.replace("-", " ");
  return "One Time";
};

const getRateSummary = (job: JobPosting) => {
  if (job.compensation?.type === "negotiable") return "Negotiable";
  const minRate = job.compensation?.minRate;
  const maxRate = job.compensation?.maxRate;
  if (!minRate) return "Rate TBD";
  return maxRate && maxRate !== minRate ? `$${minRate} - $${maxRate}` : `$${minRate}`;
};

const getBenefits = (job: JobPosting) => job.compensation?.additionalBenefits || [];
const sectionTitleClassName = "text-sm font-semibold text-gray-300";
const sectionSubheadingClassName = "text-sm font-semibold text-gray-300";

const renderActionButton = (action: JobDetailAction) => {
  const Icon = action.icon;
  const className =
    action.variant === "secondary"
      ? "border-gray-600 text-gray-300 hover:text-white hover:bg-gray-700"
      : "bg-cyan-600 hover:bg-cyan-700 text-white";

  return (
    <Button
      key={action.label}
      onClick={action.onClick}
      variant={action.variant === "secondary" ? "outline" : "default"}
      className={className}
    >
      <Icon className="mr-2 h-4 w-4" />
      {action.label}
    </Button>
  );
};

export function JobDetailPresentation({
  job,
  primaryAction,
  secondaryAction,
  extraActions = [],
  splitDetails = false,
  creatorLabel = "Job Creator",
  sidebarExtra,
  footer,
}: JobDetailPresentationProps) {
  const benefits = getBenefits(job);
  const postedDate = formatPostedDate(job.createdAt);
  const locationQuery = getLocationQuery(job);
  const readablePostedDate = formatReadableDate(job.createdAt);
  const locationCard = (
    <Card className="min-h-[344px] border-gray-700 bg-gray-800">
      <CardContent className="p-5">
        <h3 className={sectionTitleClassName}>Location</h3>
        <div className="mt-4 min-h-[268px] rounded-2xl border border-gray-700 bg-gray-900/35 p-4">
          {locationQuery ? (
            <div className="mb-4 overflow-hidden rounded-2xl border border-gray-700">
              <iframe
                title={`Map for ${job.title}`}
                src={`https://www.google.com/maps?q=${encodeURIComponent(locationQuery)}&output=embed`}
                className="h-48 w-full bg-gray-900"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          ) : (
            <div className="mb-4 flex h-48 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-600/15 to-gray-900">
              <MapPin className="h-8 w-8 text-cyan-400" />
            </div>
          )}
          <p className="text-sm font-medium text-white">{getLocationDisplay(job)}</p>
          {job.location?.venue && <p className="mt-1 text-xs text-gray-400">{job.location.venue}</p>}
          {job.location?.address && <p className="mt-3 text-xs leading-5 text-gray-400">{job.location.address}</p>}
        </div>
      </CardContent>
    </Card>
  );
  const perksCard = (
    <Card className="min-h-[206px] border-gray-700 bg-gray-800">
      <CardContent className="space-y-5 p-5">
        {(benefits.length > 0 || job.urgency) && (
          <div>
            <h3 className={sectionTitleClassName}>Perks & Benefits</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {benefits.map((benefit, index) => (
                <Badge key={index} variant="outline" className="border-green-600/30 text-green-400">
                  {benefit}
                </Badge>
              ))}
              {job.urgency && (
                <Badge variant="outline" className="border-gray-600 text-gray-300 capitalize">
                  {job.urgency}
                </Badge>
              )}
            </div>
          </div>
        )}

        <div className="border-t border-gray-700 pt-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="rounded-full bg-gray-700 p-2.5">
                <User className="h-4 w-4 text-cyan-400" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-white">{formatCreatorName(job.creatorName)}</p>
                <p className="text-xs text-gray-400">{creatorLabel}</p>
              </div>
            </div>

            <div className="text-right">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Posted</p>
              <p className="mt-1 text-sm font-medium text-gray-300">{readablePostedDate}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden border-gray-700 bg-gray-800">
        <CardContent className="p-5 sm:p-6">
          <div className="grid gap-6 lg:grid-cols-[minmax(260px,320px)_minmax(0,1fr)] lg:items-start">
            <JobVisual
              imageUrl={job.imageUrl || job.coverPhoto}
              title={job.title}
              className="w-full rounded-3xl"
            />

            <div className="space-y-6">
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-400">
                  {formatJobType(job.category)} {job.locationType === "virtual" ? "• Remote" : ""}
                </p>
                <div className="space-y-2">
                  <h1 className="text-3xl font-bold leading-tight text-white sm:text-4xl">{job.title}</h1>
                  <p className="text-sm text-gray-400">
                    {formatPostedAgo(job.createdAt)} • {job.applicants || 0} applicants
                  </p>
                </div>
                <div className="flex flex-col gap-3 pt-1 sm:flex-row">
                  {renderActionButton(primaryAction)}
                  {renderActionButton(secondaryAction)}
                  {extraActions.map((action) => renderActionButton(action))}
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-gray-700 bg-gray-900/40 p-4">
                  <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                    <DollarSign className="h-3.5 w-3.5 text-cyan-400" />
                    Price
                  </div>
                  <p className="text-xl font-semibold text-white">{getRateSummary(job)}</p>
                  <p className="mt-1 text-xs text-gray-400">{job.compensation?.type?.replace("-", " ") || "Rate details"}</p>
                </div>

                <div className="rounded-2xl border border-gray-700 bg-gray-900/40 p-4">
                  <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                    <Clock className="h-3.5 w-3.5 text-cyan-400" />
                    How Often
                  </div>
                  <p className="text-xl font-semibold capitalize text-white">{getScheduleDisplay(job)}</p>
                  <p className="mt-1 text-xs text-gray-400">
                    {job.startTime && job.endTime ? `${job.startTime} - ${job.endTime}` : "Time TBD"}
                  </p>
                </div>

                <div className="rounded-2xl border border-gray-700 bg-gray-900/40 p-4">
                  <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                    <Calendar className="h-3.5 w-3.5 text-cyan-400" />
                    Job Type
                  </div>
                  <p className="text-xl font-semibold text-white">{formatJobType(job.category)}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {splitDetails ? (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
          <Card className="border-gray-700 bg-gray-800">
            <CardContent className="p-5 sm:p-6">
              <div className="flex min-h-[340px] flex-col space-y-5">
                <div>
                  <h2 className={sectionTitleClassName}>Job Description</h2>
                  <div className="mt-4 min-h-[250px] max-h-[420px] overflow-y-auto rounded-2xl border border-gray-700 bg-gray-900/35 px-5 py-4">
                    <p className="whitespace-pre-wrap text-sm leading-7 text-gray-300 sm:text-base">
                      {job.description || "No description provided."}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {locationCard}

          <Card className="min-h-[206px] border-gray-700 bg-gray-800">
            <CardContent className="space-y-5 p-5">
              <div>
                <h3 className={sectionTitleClassName}>Dance Styles</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {job.danceStyle
                    ? job.danceStyle.split(",").map((style, index) => (
                        <Badge key={index} variant="secondary" className="border border-cyan-600/30 bg-cyan-600/20 text-cyan-300">
                          {style.trim()}
                        </Badge>
                      ))
                    : <p className="text-sm text-gray-400">No dance styles provided.</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {perksCard}
        </div>
      ) : (
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
        <div className="space-y-6">
          <Card className={`self-start border-gray-700 bg-gray-800 ${splitDetails ? "min-h-[392px]" : ""}`}>
            <CardContent className="p-5 sm:p-6">
              <div className={`flex flex-col space-y-5 ${splitDetails ? "min-h-[340px]" : "min-h-[300px]"}`}>
                <div>
                  <h2 className={sectionTitleClassName}>Job Description</h2>
                  <div className={`mt-4 max-h-[420px] overflow-y-auto rounded-2xl border border-gray-700 bg-gray-900/35 px-5 py-4 ${splitDetails ? "min-h-[250px]" : "min-h-[180px]"}`}>
                    <p className="whitespace-pre-wrap text-sm leading-7 text-gray-300 sm:text-base">
                      {job.description || "No description provided."}
                    </p>
                  </div>
                </div>

                {!splitDetails && (
                  <>
                    {job.requirements && job.requirements.length > 0 && (
                      <div>
                        <h3 className={sectionSubheadingClassName}>Responsibilities</h3>
                        <ul className="mt-4 space-y-3 text-sm text-gray-300">
                          {job.requirements.map((req, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-cyan-400" />
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {job.danceStyle && (
                      <div>
                        <h3 className={sectionSubheadingClassName}>Dance Styles</h3>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {job.danceStyle.split(",").map((style, index) => (
                            <Badge key={index} variant="secondary" className="border border-cyan-600/30 bg-cyan-600/20 text-cyan-300">
                              {style.trim()}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {job.specialInstructions && (
                      <div>
                        <h3 className={sectionSubheadingClassName}>Special Instructions</h3>
                        <div className="mt-4 rounded-2xl border border-gray-700 bg-gray-900/35 p-4">
                          <p className="text-sm text-gray-300">{job.specialInstructions}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {splitDetails && (
            <Card className="min-h-[206px] border-gray-700 bg-gray-800">
              <CardContent className="space-y-5 p-5">
                {job.danceStyle && (
                  <div>
                    <h3 className={sectionTitleClassName}>Dance Styles</h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {job.danceStyle.split(",").map((style, index) => (
                        <Badge key={index} variant="secondary" className="border border-cyan-600/30 bg-cyan-600/20 text-cyan-300">
                          {style.trim()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {!job.danceStyle && (
                  <p className="text-sm text-gray-400">No dance styles provided.</p>
                )}
              </CardContent>
            </Card>
          )}

          {footer}
        </div>

        <div className="space-y-4 self-start">
          {locationCard}

          {perksCard}

          {sidebarExtra}
        </div>
      </div>
      )}
    </div>
  );
}
